import type { AlternativeAddress } from '@interfaces/alternativeAddress';
import { CountryCodes } from '@onevesthq/ov-enums';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { getCountryName } from '@ovComponents/resources';
import { generateAlternativeAddressString } from '@utils/common';
import { unset } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AlternativeAddressField = ({
  testId,
  onChange,
  address,
  label,
  error = false,
  disabled = false,
  infoTooltip,
  lockMessage,
  locked,
}: {
  testId: string;
  address: AlternativeAddress;
  label: string;
  onChange: (e: any) => void;
  error?: boolean;
  disabled?: boolean;
  infoTooltip?: string;
  lockMessage?: string;
  locked?: boolean;
}) => {
  const { t } = useTranslation('components');
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const [alternativeAddress, setAlternativeAddress] =
    useState<AlternativeAddress>({
      line1: address?.line1 || '',
      line2: address?.line2 || '',
      city: address?.city || '',
      country: address?.country,
      postal: address?.postal || '',
      jurisdiction: address?.jurisdiction || '',
    });

  const fieldOptions: FieldOptions = useMemo(
    () => ({
      city: { required: false },
      country: { required: true },
      postal: { required: false },
      jurisdiction: { required: false },
      line1: { required: true },
      line2: { required: false },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const newErrors = validateFields(
        fieldOptions,
        alternativeAddress,
        candidateFields,
      );
      setErrors(newErrors);
      return newErrors;
    },
    [alternativeAddress, fieldOptions],
  );

  useEffect(() => {
    if (address) {
      unset(address, '__typename');
      setAlternativeAddress(address);
    }
  }, [address]);

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  const submit = () => {
    const formErrors = validate();
    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    setOpen(false);
    onChange(alternativeAddress);
  };

  const allowedCountries = useMemo(() => {
    const codes = Object.values(CountryCodes);
    return codes;
  }, []);

  const jurisdictionLabel: string = useMemo(() => {
    switch (alternativeAddress?.country) {
      case CountryCodes.CA:
        return t('components:address.province');
      case CountryCodes.US:
        return t('components:address.state');
      default:
        return t('components:address.jurisdiction');
    }
  }, [alternativeAddress?.country, t]);

  const postalCodeLabel: string = useMemo(() => {
    switch (alternativeAddress?.country) {
      case CountryCodes.CA:
        return t('components:address.postal');
      case CountryCodes.US:
        return t('components:address.zip');
      case CountryCodes.GB:
        return t('components:address.postcode');
      default:
        return t('components:address.postal');
    }
  }, [alternativeAddress?.country, t]);

  const formButtonDisabled =
    !alternativeAddress.line1 || !alternativeAddress.country;

  return (
    <>
      <TextField
        testId={testId}
        label={label}
        fullWidth
        value={generateAlternativeAddressString(alternativeAddress)}
        lockMessage={lockMessage}
        locked={locked}
        onClick={() => setOpen(true)}
        error={error}
        disabled={disabled}
        infoTooltip={infoTooltip}
      />

      <Dialog onClose={() => setOpen(false)} open={open} fullWidth>
        <DialogTitle>{t('components:address.title')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} key={t('components:address.line1') as string}>
              <TextField
                testId={testId ? `${testId}-line1` : 'line1'}
                fullWidth
                value={alternativeAddress.line1}
                label={t('components:address.line1')}
                onChange={(e: any) =>
                  setAlternativeAddress((prevState) => ({
                    ...prevState,
                    line1: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'line1'])}
                errorText={errors?.line1?.message}
                error={!!errors?.line1}
              />
            </Grid>
            <Grid item xs={12} key={t('components:address.line2') as string}>
              <TextField
                testId={testId ? `${testId}-line2` : 'line2'}
                fullWidth
                value={alternativeAddress.line2}
                label={t('components:address.line2')}
                onChange={(e: any) =>
                  setAlternativeAddress((prevState) => ({
                    ...prevState,
                    line2: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'line2'])}
                errorText={errors?.line2?.message}
                error={!!errors?.line2}
              />
            </Grid>
            <Grid item xs={6} key={t('components:address.city') as string}>
              <TextField
                testId={testId ? `${testId}-city` : 'city'}
                fullWidth
                value={alternativeAddress.city}
                label={t('components:address.city')}
                onChange={(e: any) =>
                  setAlternativeAddress((prevState) => ({
                    ...prevState,
                    city: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'city'])}
                errorText={errors?.city?.message}
                error={!!errors?.city}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField
                testId={testId ? `${testId}-country` : 'country'}
                fullWidth
                value={alternativeAddress?.country}
                label={t('components:address.country')}
                onChange={(e: any) =>
                  setAlternativeAddress((prevState) => ({
                    ...prevState,
                    country: e.target.value,
                    jurisdiction: undefined,
                  }))
                }
                onBlur={() => setFocused([...focused, 'country'])}
                errorText={errors?.country?.message}
                error={!!errors?.country}
              >
                {allowedCountries.map((cc) => (
                  <MenuItem
                    data-testid={
                      testId
                        ? `${testId}-country-${cc.toLowerCase()}`
                        : cc.toLowerCase()
                    }
                    key={cc}
                    value={cc}
                  >
                    {getCountryName(cc)}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item xs={6}>
              {postalCodeLabel && alternativeAddress?.country && (
                <TextField
                  testId={testId ? `${testId}-postal-code` : 'postal-code'}
                  fullWidth
                  value={alternativeAddress.postal}
                  label={postalCodeLabel}
                  onChange={(e: any) =>
                    setAlternativeAddress((prevState) => ({
                      ...prevState,
                      postal: e.target.value,
                    }))
                  }
                  onBlur={() => setFocused([...focused, 'postal'])}
                  errorText={errors?.postal?.message}
                  error={!!errors?.postal}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              {jurisdictionLabel && alternativeAddress?.country && (
                <TextField
                  testId={testId ? `${testId}-jurisdiction` : 'jurisdiction'}
                  fullWidth
                  value={alternativeAddress.jurisdiction}
                  label={jurisdictionLabel}
                  onChange={(e: any) =>
                    setAlternativeAddress((prevState) => ({
                      ...prevState,
                      jurisdiction: e.target.value,
                    }))
                  }
                  onBlur={() => setFocused([...focused, 'jurisdiction'])}
                  errorText={errors?.jurisdiction?.message}
                  error={!!errors?.jurisdiction}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogFooter>
          <Button
            dataTestId='manual-entry-confirm-button'
            label={t('done')}
            variant='filled'
            onClick={submit}
            disabled={formButtonDisabled}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AlternativeAddressField;
